/* GENERATED FILE — DO NOT EDIT.
 *
 * Emitted from `contracts/openapi/audit.yaml` by `tools/openapi_ts/generate.py`.
 * Regenerate with `python -m tools.openapi_ts.generate`;
 * `test_generated_client_matches_the_contract` fails the build if this file drifts from the spec.
 *
 * Hard rule 10 makes the spec authoritative, so editing this file by hand would put the client and
 * the server behind two different sources of truth — which is the drift the rule exists to prevent.
 *
 * Each type has a matching `is<Name>` guard. The types are for `tsc`; the guards are because a
 * response is data from another process, and a compile-time type is only a claim about it.
 */


/**
 * Names the record, not just the fact. "The chain is invalid" is unactionable; "sequence 4,117
 * does not hash to its stored value" is what an auditor can work from.
 */
export interface ChainDivergence {
  readonly sequence: number;
  readonly expected_hash: string;
  readonly stored_hash: string;
  /** Which of sequence continuity, linkage or digest failed. */
  readonly detail: string;
}

export function isChainDivergence(value: unknown): value is ChainDivergence {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['sequence'] === 'number' &&
    typeof candidate['expected_hash'] === 'string' &&
    typeof candidate['stored_hash'] === 'string' &&
    typeof candidate['detail'] === 'string'
  );
}

/**
 * What the walk found. Carries sequences, digests and prose only — never a value from a customer's
 * table, and never an entry payload.
 */
export interface ChainVerification {
  /**
   * `no_data` is not a failure and not a pass. Under partition-based retention an empty day is a
   * pruned day as often as a quiet one, and neither is evidence of soundness.
   */
  readonly verified: 'verified' | 'diverged' | 'no_data';
  /**
   * True for `verified` only. Exists so a client cannot reach a clean bill of health by testing
   * `verified != "diverged"`, which would treat a pruned day as sound.
   */
  readonly sound: boolean;
  readonly chain_date: string;
  /** How many entries the walk accepted before stopping. */
  readonly entries_examined: number;
  /** The hash after the last accepted entry — this day's terminal hash. */
  readonly terminal_hash: string;
  /**
   * Empty unless `audit.chain_head` describes a day `audit_entry` does not contain. This is how
   * tail truncation is detected: a chain walk stops when the rows stop and has no way to know they
   * stopped early, so the head's recorded sequence is the only witness.
   */
  readonly pointer_disagreement: string;
  /**
   * The first place the arithmetic failed, or `null`. Note that `verified` can be `diverged` with
   * `divergence` null — that is the truncation case, where nothing inside the walk broke.
   */
  readonly divergence: ChainDivergence | null;
}

export function isChainVerification(value: unknown): value is ChainVerification {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    (['verified', 'diverged', 'no_data'] as readonly unknown[]).includes(candidate['verified']) &&
    typeof candidate['sound'] === 'boolean' &&
    typeof candidate['chain_date'] === 'string' &&
    typeof candidate['entries_examined'] === 'number' &&
    typeof candidate['terminal_hash'] === 'string' &&
    typeof candidate['pointer_disagreement'] === 'string' &&
    (candidate['divergence'] === null || isChainDivergence(candidate['divergence']))
  );
}

export interface VerificationStatus {
  readonly chain_date: string;
  /**
   * Whether the *verification job* ran, which is not whether the *chain* is sound. A job that
   * succeeded may have found a divergence; read `result` for that.
   */
  readonly status: 'pending' | 'succeeded' | 'failed';
  /** Whether polling can stop. */
  readonly finished: boolean;
  /**
   * Why the job failed, when it did — the exception type and message, truncated, with the
   * traceback deliberately omitted because a traceback can carry a data value in a local
   * variable's repr.
   */
  readonly detail?: string;
  /** `null` until the job finishes. */
  readonly result: ChainVerification | null;
}

export function isVerificationStatus(value: unknown): value is VerificationStatus {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['chain_date'] === 'string' &&
    (['pending', 'succeeded', 'failed'] as readonly unknown[]).includes(candidate['status']) &&
    typeof candidate['finished'] === 'boolean' &&
    (candidate['result'] === null || isChainVerification(candidate['result']))
  );
}

/** One entry in the hash chain, **without its snapshot**. See `listAuditEntries` for why. */
export interface AuditEntry {
  /** The UTC day this entry belongs to. Part of the key, and the partition key. */
  readonly chain_date: string;
  /**
   * Position within `chain_date`, from 1. **Restarts each day**, so it is not monotone across the
   * table — which is why a page position carries both components.
   */
  readonly sequence: number;
  readonly occurred_at: string;
  /**
   * The token subject that performed the action, or a `system:` name for an unattended sweep. The
   * same string `authz.role_binding` is keyed on, so "who was allowed" and "who did it" name the
   * same person.
   */
  readonly actor: string;
  readonly action: string;
  readonly aggregate_type: string;
  readonly aggregate_id: string;
  /** The preceding entry's hash. Together these make an edit detectable. */
  readonly previous_hash: string;
  /**
   * This entry's hash. Returned so an auditor can spot-check the chain against a verification
   * result without holding the snapshot.
   */
  readonly entry_hash: string;
}

export function isAuditEntry(value: unknown): value is AuditEntry {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate['chain_date'] === 'string' &&
    typeof candidate['sequence'] === 'number' &&
    typeof candidate['occurred_at'] === 'string' &&
    typeof candidate['actor'] === 'string' &&
    typeof candidate['action'] === 'string' &&
    typeof candidate['aggregate_type'] === 'string' &&
    typeof candidate['aggregate_id'] === 'string' &&
    typeof candidate['previous_hash'] === 'string' &&
    typeof candidate['entry_hash'] === 'string'
  );
}

export interface AuditEntryPage {
  readonly items: readonly AuditEntry[];
  readonly meta: Readonly<Record<string, unknown>>;
}

export function isAuditEntryPage(value: unknown): value is AuditEntryPage {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    Array.isArray(candidate['items']) &&
    (candidate['items'] as readonly unknown[]).every((item) => isAuditEntry(item)) &&
    candidate['meta'] !== undefined
  );
}
